import api from "@/api/url";
import { sortList } from "@/utils/helpers";

export default {
    namespaced: true,
    
    state: () => {
        return {
            clients: [],
            pageData: {
                currentPage: 1,
                totalPages: 0,
                pageSize: 0,
                total: 0,
            },
            clientProjects: {
                list: [],
                pageData: {
                    currentPage: 1,
                    totalPages: 0,
                    pageSize: 50,
                    total: 0,
                },
            },
            clientInvoices: {
                _id: "",
                list: [],
                pageData: {
                    currentPage: 1,
                    totalPages: 0,
                    pageSize: 50,
                    total: 0,
                },
            },
            documents: [],
            refreshList: true,
            clientListStats: {
                totalClients: 0,
                totalOnboarded: 0,
                totalActive: 0,
            },
            clientListStatsRefreshCount: 0,
            clientCheckIns: {
                _id: "",
                list: [],
                pageData: {
                    currentPage: 1,
                    totalPages: 0,
                    pageSize: 50,
                    total: 0,
                },
            },
        };
    },

    getters: {
        clients: (state) => state.clients,
        pageData: (state) => state.pageData,
        clientNotes: (state) => state.clientNotes,
        clientProjects: (state) => state.clientProjects,
        clientInvoices: state => state.clientInvoices,
        refreshList: state => state.refreshList,
        clientListStats: state => state.clientListStats,
        clientCheckIns: state => state.clientCheckIns,
    },

    mutations: {
        set(state, [key, value]) {
            state[key] = value;
        },

        ADD_CLIENT_OPTIMISTIC(state, payload) {
            state.clients.unshift(payload); // add at the top
            state.pageData = {
                ...state.pageData,
                total: state.pageData.total + 1,
                totalPages: Math.ceil((state.pageData.total + 1) / state.pageData.pageSize),
            };
        },

        UPDATE_CLIENT_LIST(state, clientRes) {
            const index = state.clients.findIndex(i => i._id === "tempId");
            
            if (index !== -1) {
                state.clients.splice(index, 1, clientRes);
            }
        },

        REMOVE_CLIENT_ON_FAIL(state) {
            state.clients = state.clients.filter(i => i._id !== "tempId");
            state.pageData = {
                ...state.pageData,
                total: state.pageData.total - 1,
                totalPages: Math.ceil((state.pageData.total - 1) / state.pageData.pageSize),
            };
        },

        REMOVE_CLIENT_OPTIMISTIC(state, ids) {
            const updatedList = state.clients.filter(c => !ids.includes(c?._id));
            
            state.clients = updatedList ;
            state.pageData = {
                ...state.pageData,
                total: state.pageData.total - ids.length,
                totalPages: Math.ceil((state.pageData.total - ids.length) / state.pageData.pageSize),
            };
        },

        RESTORE_CLIENTS(state, clients) {
            state.clients = [...clients, ...state.clients];
            state.pageData = {
                ...state.pageData,
                total: state.pageData.total + clients.length,
                totalPages: Math.ceil((state.pageData.total + clients.length) / state.pageData.pageSize),
            };
        },
    },
    
    actions: {
        setRefreshList({ commit }, bool) {
            commit("set", ["refreshList", bool]);
        },

        addOptimisticUpdateList({ commit }, payload) {            
            const tempClient = {
                _id: "tempId",
                ...payload,
            };

            commit("ADD_CLIENT_OPTIMISTIC", tempClient);
        },

        addClient({ commit }, payload) {
            return api.client.create(payload)
                .then((resp) => {
                    commit("UPDATE_CLIENT_LIST", resp?.data?.data);
                    return resp.data.data;
                }).catch(error => {
                    // rollback update
                    commit("REMOVE_CLIENT_ON_FAIL");
                    throw error;
                });
        },

        fetchClients({ commit, state }, { params, refresh, config = {}, external = false }) {
            if (!refresh && state.clients.length) return;

            return api.client.getAll(params, config)
                .then((resp) => {
                    if (external) {
                        return resp?.data?.data?.data;
                    }

                    if (config?.responseType) {
                        return resp?.data;
                    } 

                    const data = resp?.data?.data;

                    commit("set", ["clients", data.data]);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        ... data.pageDetails,
                    }]);
                });
        },

        getClient(_, id) { 
            return api.client.getOne(id)
                .then((resp) => {
                    const data = resp?.data?.data;
                    return data;
                });
        },

        updateClient({ state, commit }, payload) {
            return api.client.updateOne(payload?._id, payload)
                .then((resp) => {
                    const data = resp?.data?.data;
                    const clients = state.clients;

                    const updatedClients = clients.map(c => {
                        if (c?._id === payload?._id) {
                            return {
                                ...c,
                                ...data,
                            };
                        } else {
                            return c;
                        }
                    });
        
                    commit("set", ["clients", updatedClients]);  
                    return data;
                });
        },

        archiveClient({ state, commit }, id) {
            return api.client.archive(id)
                .then((resp) => {
                    const data = resp.data.data;
                    const clients = state.clients;

                    const updatedClients = clients.filter(c => c?._id !== data?._id);

                    // update client list
                    commit("set", ["clients", updatedClients]); 
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total - 1,
                        totalPages: Math.ceil((state.pageData.total - 1) / 50),
                    }]);
                });
        },

        deleteOptimisticUpdateList({ commit }, ids) {           
            commit("REMOVE_CLIENT_OPTIMISTIC", ids);
        },

        deleteClients({ commit }, { clientsToDelete, ids }) {            
            return api.client.delete({ ids })
                .then((resp) => {})
                .catch(error => {
                    commit("RESTORE_CLIENTS", clientsToDelete);
                    throw error;
                });
        },

        uploadMutlipleClients({ state, commit }, { payload, config, fileType }) {            
            return api.client.upload(payload, { fileType }, config)
                .then((resp) => {
                    const data = resp?.data?.data ? resp?.data?.data[0] : [];

                    commit("set", ["clients", [ ...data, ...state.clients ]]);  
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total + data.length,
                        totalPages: Math.ceil((state.pageData.total + data.length) / state.pageData.pageSize),
                    }]);
                });
        },

        followUpWithClient(_, payload) {
            return api.client.followUp(payload)
                .then((resp) => {});
        },

        sortClients({ commit, state }, sortVal) {
            const copiedArr = state.clients.slice();
            
            sortList(sortVal, copiedArr, "name");
                        
            commit("set", ["clients", copiedArr]);
        },

        duplicateClient({ state, commit }, payload) {      
            // TO DO: optimistic update

            return api.client.duplicate(payload._id, payload)
                .then((resp) => {
                    const data = resp.data?.data;

                    const currentClients = state.clients;

                    commit("set", ["clients", [ data, ...currentClients ]]);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total + 1,
                        totalPages: Math.ceil((state.pageData.total + 1) / state.pageData.pageSize),
                    }]);

                    return data;
                }).catch(error => {
                    // rollback update
                    commit("set", ["clients", currentClients]);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total - 1,
                        totalPages: Math.ceil((state.pageData.total - 1) / state.pageData.pageSize),
                    }]);
                    throw error; // Re-throw for component handling
                });
        },

        fetchStats({ commit, state }, { params = {}, refresh = true }) {
            // if (!state.clientListStatsRefreshCount && refresh) return;

            /* 
                use cases:
                - user lands on the page for the FIRST TIME. count = 0; refresh: true ====> true
                - user lands on the page repeatedly, not the first time. count = n + 1; refresh: true (always going to be true)
            */
            
            return api.client.fetchStats(params)
                .then((resp) => {
                    const data = resp.data.data;

                    const arrItem = data[0];
                    let stats = {};
                    
                    for (const [key, value] of Object.entries(arrItem)) {
                        if (key && value?.length) {
                            stats[key] = value[0]?.total || 0;
                        }
                    }
                    
                    commit("set", ["clientListStats", { 
                        ...state.clientListStats, 
                        ...stats,
                    }]);
                    commit("set", ["clientListStatsRefreshCount", state.clientListStatsRefreshCount + 1]);
                });
        },

        // ------------- CHECK-INS ------------------
        getClientCheckIns({ commit, state }, { id, params }) {
            return api.client.getCheckIns(id, params)
                .then((resp) => {
                    const data = resp.data.data;

                    commit("set", ["clientCheckIns", { 
                        ...state.clientCheckIns, 
                        list: data.data, 
                        pageData: data.pageDetails,
                    }]);
                });
        },

        addCheckIn({ state, commit }, payload) {
            const currentList = state.clientCheckIns.list;

            return api.client.addCheckIn(payload)
                .then((resp) => {                     
                    commit("set", ["clientCheckIns", {
                        ...state.clientCheckIns,
                        list: [{ ...payload, ...resp.data.data }, ...currentList],
                        pageData: {
                            ...state.clientCheckIns.pageData,
                            total: state.clientCheckIns.pageData.total + 1,
                            totalPages: Math.ceil((state.clientCheckIns.pageData.total + 1) / state.clientCheckIns.pageData.pageSize),
                        },
                    }]);
                });
        },

        removeCheckIn(_, id) {
            return api.client.removeCheckIn(id)
                .then(() => {
                    const list = state.clientCheckIns.list;

                    commit("set", ["clientCheckIns", {
                        ...state.clientCheckIns,
                        list: list.filter(x => x._id !== projectId),
                        pageData: {
                            ...state.clientCheckIns.pageData,
                            total: state.clientCheckIns.pageData.total - 1,
                            totalPages: Math.ceil((state.clientCheckIns.pageData.total - 1) / state.clientCheckIns.pageData.pageSize),
                        },
                    }]);  
                }); 
        },

        // ------------- NOTES ------------------
        getClientNotes(_, { id, params }) {
            return api.client.getNotes(id, params)
                .then((resp) => {
                    return resp.data.data;
                });
        },

        addNewNote(_, payload) {
            return api.client.createNote(payload)
                .then((resp) => {
                    return resp.data.data;
                });
        },

        updateNote(_, payload) {
            return api.client.editNote(payload.refId, payload)
                .then((resp) => {
                    return resp.data.data;
                });
        },

        deleteNote(_, payload) {
            return api.client.deleteNote(payload.clientId, payload)
                .then((resp) => {
                    return resp.data.data;
                });
        },

        // -------------- PROJECTS ----------------
        getClientProjects({ commit, state }, { id, params, external = false }) {
            return api.client.getProjects(id, params)
                .then((resp) => {
                    const data = resp.data.data;

                    if (external) {
                        return data.data;
                    }

                    commit("set", ["clientProjects", { 
                        ...state.clientProjects, 
                        list: data.data, 
                        pageData: data.pageDetails,
                    }]);
                });
        },

        addNewProject({ state, commit }, payload) {
            const currentProjects = state.clientProjects.list;
            return api.project.create(payload)
                .then((resp) => {                     
                    commit("set", ["clientProjects", {
                        ...state.clientProjects,
                        list: [{ ...payload, ...resp.data.data }, ...currentProjects],
                        pageData: {
                            ...state.clientProjects.pageData,
                            total: state.clientProjects.pageData.total + 1,
                            totalPages: Math.ceil((state.clientProjects.pageData.total + 1) / state.clientProjects.pageData.pageSize),
                        },
                    }]);
                    return resp.data.data;
                }).catch(error => {
                    throw error; // Re-throw for component handling
                });
        },

        addExistingroject({ state, commit }, { clientId, payload }) {
            const currentProjects = state.clientProjects.list;

            commit("set", ["clientProjects", {
                ...currentProjects,
                list: [payload, ...currentProjects],
                pageData: {
                    ...state.clientProjects.pageData,
                    total: state.clientProjects.pageData.total + 1,
                    totalPages: Math.ceil((state.clientProjects.pageData.total + 1) / state.clientProjects.pageData.pageSize),
                },
            }]);

            return api.client.addProject(clientId, { projectId: payload._id })
                .then((resp) => {
                    return resp.data.data;
                }).catch(error => {
                    // rollback update
                    commit("set", ["clientProjects", {
                        ...state.clientProjects,
                        list: currentProjects,
                        pageData: {
                            ...state.clientProjects.pageData,
                            total: state.clientProjects.pageData.total - 1,
                            totalPages: Math.ceil((state.clientProjects.pageData.total - 1) / state.clientProjects.pageData.pageSize),
                        },
                    }]);
                    throw error; // Re-throw for component handling
                });
        },

        deleteProject({ state, commit }, { clientId, projectId }) {
            return api.client.removeProject(clientId, { projectId })
                .then(() => {
                    const projects = state.clientProjects.list;

                    commit("set", ["clientProjects", {
                        ...state.clientProjects,
                        list: projects.filter(x => x._id !== projectId),
                        pageData: {
                            ...state.clientProjects.pageData,
                            total: state.clientProjects.pageData.total - 1,
                            totalPages: Math.ceil((state.clientProjects.pageData.total - 1) / state.clientProjects.pageData.pageSize),
                        },
                    }]);  
                }); 
        },
        
        // ------------- INVOICES ------------------
        getClientInvoices({ commit, state }, { id, params }) {
            return api.client.getInvoices(id, params)
                .then((resp) => {
                    const data = resp.data.data;
                    
                    commit("set", ["clientInvoices", { 
                        ...state.clientInvoices, 
                        list: data.data, 
                        _id: id,
                        pageData: data.pageDetails,
                    }]);
                });
        },

        sortInvoices({ commit, state }, sortVal) {
            const copiedArr = state.invoicesData.invoices.slice();
            sortList(sortVal, copiedArr, "status");
            commit("set", ["invoicesData", { ...state.invoicesData, invoices: copiedArr }]);
        },

        // ------------- LINKS ------------------
        fetchLinks(_, clientId) {
            return api.client.fetchLinks(clientId)
                .then((resp) => {                    
                    return resp?.data?.data;  
                });
        },

        manageLinks(_, { clientId, payload, params }) {
            console.log({ payload, params });
            
            return api.client.manageLinks(clientId, params, payload)
                .then((resp) => {
                    const data = resp?.data?.data;                    
                    return data;      
                });
        },
        
    },
};